import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { Badge } from './Badge';
import { Skeleton } from './Skeleton';
import { useCheckpointPreview } from '../helpers/useCheckpointsApi';
import { CodeMirrorEditor as Editor } from './CodeMirrorEditor';
import { FileCode, Calendar, User, GitCommit, RotateCcw, X, FileText } from 'lucide-react';
import styles from './CheckpointPreviewDialog.module.css';

interface CheckpointPreviewDialogProps {
  checkpointId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore?: () => void;
}

export const CheckpointPreviewDialog = ({
  checkpointId,
  open,
  onOpenChange,
  onRestore,
}: CheckpointPreviewDialogProps) => {
  const { data, isLoading, error } = useCheckpointPreview(open ? checkpointId : null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // Reset selected file when data loads
  useEffect(() => {
    if (data?.checkpoint?.filesSnapshot && data.checkpoint.filesSnapshot.length > 0) {
      setSelectedFile(data.checkpoint.filesSnapshot[0].filePath);
    }
  }, [data]);

  const handleRestore = () => {
    if (onRestore) {
      if (
        window.confirm(
          'Are you sure you want to restore this checkpoint? Current changes will be overwritten.',
        )
      ) {
        onRestore();
        onOpenChange(false);
      }
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Unknown date';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(date));
  };

  const getLanguageFromFileName = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js':
        return 'javascript';
      case 'ts':
        return 'typescript';
      case 'jsx':
        return 'javascript';
      case 'tsx':
        return 'typescript';
      case 'css':
        return 'css';
      case 'html':
        return 'html';
      case 'json':
        return 'json';
      case 'py':
        return 'python';
      case 'md':
        return 'markdown';
      default:
        return 'plaintext';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialogContent}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <DialogHeader>
              <Skeleton className={styles.titleSkeleton} />
              <Skeleton className={styles.descSkeleton} />
            </DialogHeader>
            <div className={styles.metaSkeleton}>
              <Skeleton className={styles.badgeSkeleton} />
              <Skeleton className={styles.badgeSkeleton} />
            </div>
            <div className={styles.editorSkeleton} />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <DialogHeader>
              <DialogTitle>Error loading checkpoint</DialogTitle>
              <DialogDescription>
                {(error as Error).message || 'Failed to load checkpoint details.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : data?.checkpoint ? (
          <>
            <DialogHeader>
              <div className={styles.headerTop}>
                <DialogTitle className={styles.title}>
                  <GitCommit className={styles.icon} size={20} />
                  {data.checkpoint.name}
                </DialogTitle>
                <Badge variant="outline" className={styles.typeBadge}>
                  {data.checkpoint.checkpointType}
                </Badge>
              </div>

              <div className={styles.metadataRow}>
                <div className={styles.metaItem}>
                  <Calendar size={14} />
                  <span>{formatDate(data.checkpoint.createdAt)}</span>
                </div>
                {data.checkpoint.createdBy && (
                  <div className={styles.metaItem}>
                    <User size={14} />
                    <span>User ID: {data.checkpoint.createdBy}</span>
                  </div>
                )}
              </div>

              {data.checkpoint.description && (
                <DialogDescription className={styles.description}>
                  {data.checkpoint.description}
                </DialogDescription>
              )}
            </DialogHeader>

            <div className={styles.contentArea}>
              {data.checkpoint.filesSnapshot && data.checkpoint.filesSnapshot.length > 0 ? (
                <Tabs
                  value={selectedFile || undefined}
                  onValueChange={setSelectedFile}
                  className={styles.tabs}
                >
                  <TabsList className={styles.tabsList}>
                    {data.checkpoint.filesSnapshot.map((file) => (
                      <TabsTrigger
                        key={file.filePath}
                        value={file.filePath}
                        className={styles.tabTrigger}
                      >
                        <FileCode size={14} className={styles.fileIcon} />
                        {file.fileName}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {data.checkpoint.filesSnapshot.map((file) => (
                    <TabsContent
                      key={file.filePath}
                      value={file.filePath}
                      className={styles.tabContent}
                    >
                      <div className={styles.editorContainer}>
                        <Editor
                          height="100%"
                          language={file.language || getLanguageFromFileName(file.fileName)}
                          value={file.content}
                          theme="tasking-dark"
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            fontSize: 13,
                            fontFamily: "'JetBrains Mono', monospace",
                            scrollBeyondLastLine: false,
                            lineNumbers: 'on',
                            renderValidationDecorations: 'off',
                            domReadOnly: true,
                          }}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              ) : (
                <div className={styles.emptyState}>
                  <FileText size={48} />
                  <p>No files in this checkpoint</p>
                </div>
              )}
            </div>

            <DialogFooter className={styles.footer}>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {onRestore && (
                <Button onClick={handleRestore} className={styles.restoreButton}>
                  <RotateCcw size={16} />
                  Restore Checkpoint
                </Button>
              )}
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
